// Сравнение конфигов по ОДНИМ И ТЕМ ЖЕ seed.
//
// Главное здесь — список перевернувшихся seed, а не сдвиг средних.
// Поэтому список стоит первым и занимает большую часть страницы, а сдвиг
// медиан убран вниз как справка: правку делают ради тех тридцати seed,
// которые перешли черту, и среднее их прячет.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { pairFromParams, withPairSide } from '../lib/pair';
import { useStore } from '../data/store';
import { Callout, CopyButton, OriginTag, Panel, Tile } from '../components/Ui';
import { Histogram } from '../components/Charts';
import { compare, describe, histogram, METRICS } from '../lib/stats';
import { loadComparison, loadRuns, reproduceCommand } from '../data/source';
import type { ComparisonExtras } from '../data/normalize';
import { num, percent, signed } from '../lib/format';
import type { RunRow } from '../lib/types';

const FLIP_LABELS: Record<string, string> = {
  deficit: 'дефицит патронов',
  no_enemies: 'уровень без врагов',
  fallback: 'запасная планировка',
  flags: 'набор флагов',
};

export function Compare() {
  const store = useStore();
  const datasets = store.datasets;
  // Пара живёт в адресе (`?a=&b=`, PLAN.md B2a): ссылка открывает то же сравнение.
  const [params, setParams] = useSearchParams();
  const { a, b } = useMemo(() => pairFromParams(params, datasets), [params, datasets]);
  const setA = (name: string) => setParams((current) => withPairSide(current, 'a', name), { replace: true });
  const setB = (name: string) => setParams((current) => withPairSide(current, 'b', name), { replace: true });
  const [rowsA, setRowsA] = useState<RunRow[]>([]);
  const [rowsB, setRowsB] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [metric, setMetric] = useState('enemies');
  const [shiftLimit, setShiftLimit] = useState(50);
  const [extras, setExtras] = useState<ComparisonExtras | null>(null);

  useEffect(() => {
    const server = store.server;
    if (server === null || a === null || b === null) return;
    let alive = true;
    setLoading(true);
    setFailure(null);
    void (async () => {
      try {
        const [left, right] = await Promise.all([
          loadRuns(server.online, a),
          loadRuns(server.online, b),
        ]);
        if (!alive) return;
        setRowsA(left.value);
        setRowsB(right.value);
        const comparison = await loadComparison(server.online, a, b);
        if (alive) setExtras(comparison.value);
      } catch (error) {
        if (alive) setFailure(error instanceof Error ? error.message : String(error));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [store.server, a, b]);

  const result = useMemo(() => compare(rowsA, rowsB), [rowsA, rowsB]);
  const chosen = METRICS.find((m) => m.key === metric) ?? METRICS[0];

  const statsA = useMemo(() => describe(rowsA.map((r) => (chosen ? chosen.pick(r) : 0))), [rowsA, chosen]);
  const binsA = useMemo(() => histogram(rowsA.map((r) => (chosen ? chosen.pick(r) : 0))), [rowsA, chosen]);
  const binsB = useMemo(() => histogram(rowsB.map((r) => (chosen ? chosen.pick(r) : 0))), [rowsB, chosen]);

  const shifts = useMemo(() => result.shiftsFor(metric), [result, metric]);

  const flipsByKind = useMemo(() => {
    const counts = new Map<string, number>();
    for (const flip of result.flips) counts.set(flip.kind, (counts.get(flip.kind) ?? 0) + 1);
    return [...counts.entries()].sort((x, y) => y[1] - x[1]);
  }, [result.flips]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Сравнение конфигов</h1>
        <p>
          Сравнение идёт по общим seed. Разные конфиги на одном seed дают разные уровни — и ровно
          поэтому важен не сдвиг медианы, а число seed, перешедших черту.
        </p>
        <OriginTag origin={store.runsOrigin} />
      </div>

      <Panel
        title="Что с чем"
        aside={
          <div className="controls">
            <label className="field">
              A
              <select value={a ?? ''} onChange={(e) => setA(e.target.value)}>
                {datasets.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name} · {d.rows} · {d.configHash?.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              B
              <select value={b ?? ''} onChange={(e) => setB(e.target.value)}>
                {datasets.map((d) => (
                  <option key={d.name} value={d.name}>
                    {d.name} · {d.rows} · {d.configHash?.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      >
        {failure && (
          <Callout tone="hard" title="Не удалось загрузить выборки">
            <p className="mono">{failure}</p>
          </Callout>
        )}

        {result.sameConfig && (
          <Callout tone="hard" title="Отпечатки конфигов совпадают">
            <p>
              Обе выборки посчитаны с отпечатком <span className="mono">{result.hashA}</span>: это одна
              и та же игра, и переворотов быть не может по построению. Возьмите датасеты с разными
              отпечатками, иначе страница честно покажет ноль и ничего не скажет.
            </p>
          </Callout>
        )}

        {(result.hashA === null || result.hashB === null) && (
          <Callout tone="hard" title="Внутри выборки перемешаны конфиги">
            <p>
              У одной из сторон больше одного отпечатка. Раздел 5 контракта запрещает складывать такие
              строки в одну статистику — сравнивать их тем более нельзя.
            </p>
          </Callout>
        )}

        <div className="tiles">
          <Tile
            label="Общих seed"
            value={num(result.sharedSeeds)}
            note={`только в A: ${num(result.onlyInA)} · только в B: ${num(result.onlyInB)}`}
          />
          <Tile
            label="Перевернулось seed"
            value={num(result.flips.length)}
            tone={result.flips.length > 0 ? 'warn' : 'good'}
            note={
              result.sharedSeeds > 0
                ? `${percent(result.flips.length / result.sharedSeeds)} от общих`
                : 'общих seed нет'
            }
          />
          <Tile
            label={`Сдвинулось по «${chosen?.label ?? ''}»`}
            value={num(shifts.length)}
            note={
              result.sharedSeeds > 0
                ? `${percent(shifts.length / result.sharedSeeds)} общих seed изменили значение`
                : '—'
            }
          />
          {flipsByKind.map(([kind, count]) => (
            <Tile
              key={kind}
              label={FLIP_LABELS[kind] ?? kind}
              value={num(count)}
              tone={kind === 'deficit' || kind === 'no_enemies' ? 'bad' : 'warn'}
              note="seed, где признак поменялся"
            />
          ))}
        </div>
      </Panel>

      {extras !== null && extras.changes.length > 0 && (
        <Panel
          title={`Величины сменили блок измерения — ${num(extras.changes.length)}`}
          aside={<span className="dim">самое важное отличие двух выборок</span>}
        >
          <p style={{ marginTop: 0, fontSize: 'var(--fs-sm)' }}>
            Инструмент измерения выбирается по выборке, а не прибит к метрике. Если величина
            расползлась и перестала быть тесной — это ответ на вопрос «что изменилось» точнее, чем
            любой сдвиг медианы.
          </p>
          {extras.changes.map((change) => (
            <div
              key={change.metric}
              style={{
                borderLeft: '3px solid var(--amber)',
                background: 'var(--bg-2)',
                padding: '9px 12px',
                marginBottom: 8,
                borderRadius: 'var(--radius)',
              }}
            >
              <div style={{ fontSize: 'var(--fs-sm)', marginBottom: 3 }}>
                {change.phrase ?? `${change.label}: ${change.blockA} → ${change.blockB}`}
              </div>
              <div className="mono faint" style={{ fontSize: 'var(--fs-micro)' }}>
                {change.label} · A: {change.blockA ?? change.classificationA} (
                {num(change.distinctA)} значений) → B: {change.blockB ?? change.classificationB} (
                {num(change.distinctB)} значений)
              </div>
            </div>
          ))}
        </Panel>
      )}

      {extras !== null && extras.outlierWarning !== null && (
        <Callout tone="hard" title="Число выбросов двух выборок напрямую не сравнивается">
          <p>{extras.outlierWarning}</p>
        </Callout>
      )}

      <Panel
        title={`Перевернувшиеся seed — ${num(result.flips.length)}`}
        flush
        aside={
          result.flips.length > 0 ? (
            <CopyButton
              text={result.flips.map((f) => f.sentence).join('\n')}
              label="копировать список"
            />
          ) : undefined
        }
      >
        {loading ? (
          <div className="empty">загрузка…</div>
        ) : result.flips.length === 0 ? (
          <div className="empty">
            {result.sharedSeeds === 0
              ? 'общих seed нет — сравнивать нечего'
              : `ни один из ${num(result.sharedSeeds)} общих seed не перешёл черту: дефицит патронов, наличие врагов, запасная планировка и флаги остались прежними. Правка сдвинула числа, и какие именно seed сдвинулись — в таблице ниже.`}
          </div>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="n">seed</th>
                  <th>Что перевернулось</th>
                  <th>Было → стало</th>
                  <th>Фраза</th>
                  <th>Числа</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {result.flips.map((flip) => (
                  <tr key={flip.seed}>
                    <td className="n mono">{flip.seed}</td>
                    <td>
                      <span className={flip.kind === 'deficit' || flip.kind === 'no_enemies' ? 'tag bad' : 'tag warn'}>
                        {FLIP_LABELS[flip.kind] ?? flip.kind}
                      </span>
                    </td>
                    <td className="mono dim">
                      {flip.before} → <span className="warn">{flip.after}</span>
                    </td>
                    <td>{flip.sentence}</td>
                    <td className="mono faint">
                      {flip.changes
                        .slice(0, 4)
                        .map((c) => `${c.label}: ${c.before}→${c.after}`)
                        .join(' · ')}
                    </td>
                    <td>
                      <CopyButton text={reproduceCommand(b ?? '', flip.seed)} label="повторить" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title={`Сдвинувшиеся seed по величине «${chosen?.label ?? ''}» — ${num(shifts.length)}`}
        flush
        aside={
          shifts.length > 0 ? (
            <CopyButton
              text={shifts
                .map((s) => `seed ${s.seed}: ${chosen?.label} ${s.before} → ${s.after} (${signed(s.delta)})`)
                .join('\n')}
              label="копировать список"
            />
          ) : undefined
        }
      >
        {shifts.length === 0 ? (
          <div className="empty">
            по этой величине не сдвинулся ни один из {num(result.sharedSeeds)} общих seed
          </div>
        ) : (
          <div className="scroll" style={{ maxHeight: '38vh' }}>
            <table>
              <thead>
                <tr>
                  <th className="n">seed</th>
                  <th className="n">A</th>
                  <th className="n">B</th>
                  <th className="n">сдвиг</th>
                  <th>Насколько это много</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shifts.slice(0, shiftLimit).map((shift) => (
                  <tr key={shift.seed}>
                    <td className="n mono">{shift.seed}</td>
                    <td className="n dim">{num(shift.before)}</td>
                    <td className="n">{num(shift.after)}</td>
                    <td className={shift.delta > 0 ? 'n warn' : 'n good'}>{signed(shift.delta)}</td>
                    <td className="dim">
                      {shift.before !== 0
                        ? `${signed((shift.delta / Math.abs(shift.before)) * 100, 0)}% от значения в A`
                        : 'в A было ноль'}
                    </td>
                    <td>
                      <CopyButton text={reproduceCommand(b ?? '', shift.seed)} label="повторить" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shifts.length > shiftLimit && (
              <div style={{ padding: 8 }}>
                <button className="action" onClick={() => setShiftLimit((l) => l + 100)}>
                  показать ещё ({num(shifts.length - shiftLimit)} осталось)
                </button>
              </div>
            )}
          </div>
        )}
      </Panel>

      <Panel
        title="Форма распределения: A против B"
        aside={
          <label className="field">
            величина
            <select value={metric} onChange={(e) => setMetric(e.target.value)}>
              {METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        }
      >
        <Histogram bins={binsA} overlay={binsB} stats={statsA} unit={chosen?.unit ?? ''} />
        <div className="legend">
          <span>
            <i style={{ background: 'var(--series-a)' }} />
            A · {a}
          </span>
          <span>
            <i style={{ border: '1px solid var(--series-b)' }} />
            B · {b} (контур)
          </span>
          <span className="faint">перцентили на графике — от выборки A</span>
        </div>
      </Panel>

      <Panel title="Сдвиг медиан — справка, а не вывод" flush>
        <table>
          <thead>
            <tr>
              <th>Величина</th>
              <th className="n">A</th>
              <th className="n">B</th>
              <th className="n">сдвиг</th>
            </tr>
          </thead>
          <tbody>
            {result.medianShift.map((shift) => (
              <tr key={shift.metric}>
                <td>{shift.label}</td>
                <td className="n">{num(shift.a)}</td>
                <td className="n">{num(shift.b)}</td>
                <td className={shift.delta === 0 ? 'n faint' : 'n warn'}>{signed(shift.delta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
